import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Chip, Alert,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, PlayArrow as ActivateIcon, Visibility as ViewIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

interface QuestionForm {
  text: string;
  type: 'number' | 'text';
  required: boolean;
  min?: number | '';
  max?: number | '';
}

interface Survey {
  _id: string;
  title: string;
  description?: string;
  questions: Array<{
    text: string;
    type: 'number' | 'text';
    required: boolean;
    min?: number | null;
    max?: number | null;
  }>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const Surveys: React.FC = () => {
  const { hasPermission } = useAdminAuth();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [editing, setEditing] = useState<Survey | null>(null);
  const [openResponses, setOpenResponses] = useState<boolean>(false);
  const [responses, setResponses] = useState<any[]>([]);
  const [responsesSurvey, setResponsesSurvey] = useState<Survey | null>(null);
  const [openResponseDetail, setOpenResponseDetail] = useState<boolean>(false);
  const [selectedResponse, setSelectedResponse] = useState<any | null>(null);
  const [responsesLoading, setResponsesLoading] = useState<boolean>(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [surveyActive, setSurveyActive] = useState<boolean>(false);
  const [questions, setQuestions] = useState<QuestionForm[]>([]);

  const API_BASE = (window as any).__API_URL__ || process.env.REACT_APP_API_URL || '/api';
  const api = axios.create({ baseURL: API_BASE });
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  const fetchSurveys = async () => {
    try {
      setLoading(true);
      const res = await api.get('/surveys');
      setSurveys(res.data.data || []);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSurveys(); }, []);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setSurveyActive(false);
    setQuestions([]);
    setEditing(null);
  };

  const handleOpen = (survey?: Survey) => {
    if (survey) {
      setEditing(survey);
      setTitle(survey.title);
      setDescription(survey.description || '');
      setSurveyActive(!!survey.isActive);
      setQuestions(
        survey.questions.map(q => ({
          text: q.text,
          type: q.type,
          required: q.required,
          min: q.min ?? '',
          max: q.max ?? '',
        }))
      );
    } else {
      resetForm();
    }
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    resetForm();
  };

  const addQuestion = () => {
    setQuestions(prev => [...prev, { text: '', type: 'text', required: true }]);
  };

  const updateQuestion = (index: number, patch: Partial<QuestionForm>) => {
    setQuestions(prev => prev.map((q, i) => i === index ? { ...q, ...patch } : q));
  };

  const removeQuestion = (index: number) => {
    setQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      const fd = new FormData();
      fd.append('title', title);
      if (description) fd.append('description', description);
      fd.append('isActive', String(surveyActive));
      const toSend = questions.map(q => ({
        text: q.text,
        type: q.type,
        required: !!q.required,
        min: q.type === 'number' && q.min !== '' ? Number(q.min) : null,
        max: q.type === 'number' && q.max !== '' ? Number(q.max) : null,
      }));
      fd.append('questions', JSON.stringify(toSend));

      if (editing) {
        // Для спрощення MVP: видалити і створити заново не реалізуємо. Потрібен PATCH /surveys/:id (не додавали).
        // Тому поки лише create
      }

      await api.post('/surveys', fd);
      await fetchSurveys();
      handleClose();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Помилка збереження');
    }
  };

  const handleActivate = async (survey: Survey, isActive: boolean) => {
    try {
      const fd = new FormData();
      fd.append('isActive', String(isActive));
      const res = await api.patch(`/surveys/${survey._id}/active`, fd);
      await fetchSurveys();
      // Якщо активуємо нове — ок
    } catch (e: any) {
      setError(e.response?.data?.message || 'Помилка оновлення статусу');
    }
  };

  const handleDelete = async (survey: Survey) => {
    if (!window.confirm('Ви впевнені, що хочете видалити це опитування?')) return;
    try {
      await api.delete(`/surveys/${survey._id}`);
      await fetchSurveys();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Помилка видалення');
    }
  };

  const handleOpenResponses = async (survey: Survey) => {
    try {
      setResponses([]);
      setResponsesLoading(true);
      const res = await api.get(`/surveys/${survey._id}/responses`);
      setResponses(res.data.data || []);
      setResponsesSurvey(survey);
      setOpenResponses(true);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Помилка отримання відповідей');
    } finally {
      setResponsesLoading(false);
    }
  };

  const openResponse = (resp: any) => {
    setSelectedResponse(resp);
    setOpenResponseDetail(true);
  };

  const renderAnswerValue = (a: any) => {
    if (a?.valueNumber !== null && a?.valueNumber !== undefined) return String(a.valueNumber);
    if (a?.valueText) return a.valueText;
    if (a?.value !== undefined) return String(a.value);
    return '—';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Опитування</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>Створити</Button>
      </Box>

      {error && (<Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>)}

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Питань</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {surveys.map(s => (
                <TableRow key={s._id}>
                  <TableCell>{s.title}</TableCell>
                  <TableCell>{s.questions?.length || 0}</TableCell>
                  <TableCell>
                    <Chip label={s.isActive ? 'Активне' : 'Неактивне'} color={s.isActive ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" onClick={() => handleOpen(s)}><EditIcon /></IconButton>
                    <IconButton color="primary" onClick={() => handleOpenResponses(s)}><ViewIcon /></IconButton>
                    <IconButton color="warning" onClick={() => handleActivate(s, !s.isActive)}><ActivateIcon /></IconButton>
                    <IconButton color="error" onClick={() => handleDelete(s)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Редагувати опитування' : 'Створити опитування'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 2, mt: 1 }}>
            <TextField label="Назва" value={title} onChange={e => setTitle(e.target.value)} fullWidth required />
            <TextField label="Опис" value={description} onChange={e => setDescription(e.target.value)} fullWidth multiline rows={3} />
            <FormControlLabel
              control={<Switch checked={surveyActive} onChange={(e) => setSurveyActive(e.target.checked)} color="success" />}
              label="Активне опитування"
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Питання</Typography>
            {questions.map((q, idx) => (
              <Card key={idx} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                    <TextField label="Текст питання" value={q.text} onChange={e => updateQuestion(idx, { text: e.target.value })} fullWidth required />
                    <FormControl fullWidth>
                      <InputLabel>Тип відповіді</InputLabel>
                      <Select
                        label="Тип відповіді"
                        value={q.type}
                        onChange={(e) => updateQuestion(idx, { type: e.target.value as 'text' | 'number' })}
                      >
                        <MenuItem value="text">Текст</MenuItem>
                        <MenuItem value="number">Число</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControlLabel
                      control={<Switch checked={q.required} onChange={(e) => updateQuestion(idx, { required: e.target.checked })} color="primary" />}
                      label="Обов'язкове питання"
                    />
                    {q.type === 'number' && (
                      <>
                        <TextField label="Мінімум" type="number" value={q.min as any} onChange={e => updateQuestion(idx, { min: e.target.value === '' ? '' : Number(e.target.value) })} fullWidth />
                        <TextField label="Максимум" type="number" value={q.max as any} onChange={e => updateQuestion(idx, { max: e.target.value === '' ? '' : Number(e.target.value) })} fullWidth />
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button color="error" onClick={() => removeQuestion(idx)}>Видалити питання</Button>
                  </Box>
                </CardContent>
              </Card>
            ))}
            <Button variant="outlined" onClick={addQuestion}>Додати питання</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave}>Зберегти</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openResponses} onClose={() => setOpenResponses(false)} maxWidth="md" fullWidth>
        <DialogTitle>Відповіді опитування</DialogTitle>
        <DialogContent>
          {responsesLoading ? (
            <Typography>Завантаження...</Typography>
          ) : responses.length === 0 ? (
            <Alert severity="info">Немає відповідей</Alert>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Батько</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Садок</TableCell>
                  <TableCell>Дата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {responses.map((r: any) => (
                  <TableRow key={r._id} hover sx={{ cursor: 'pointer' }} onClick={() => openResponse(r)}>
                    <TableCell>{r.parent?.fullName || '—'}</TableCell>
                    <TableCell>{r.user?.email || r.parent?.email || '—'}</TableCell>
                    <TableCell>{r.kindergarten?.name || '—'}</TableCell>
                    <TableCell>{new Date(r.createdAt).toLocaleString('uk-UA')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResponses(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openResponseDetail} onClose={() => setOpenResponseDetail(false)} maxWidth="md" fullWidth>
        <DialogTitle>Відповіді (деталі)</DialogTitle>
        <DialogContent>
          {selectedResponse && responsesSurvey ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {responsesSurvey.questions.map((q, idx) => {
                const answer = selectedResponse.answers?.find((a: any) => a.questionIndex === idx) || null;
                return (
                  <Card key={idx}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {idx + 1}. {q.text}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Тип: {q.type === 'number' ? 'Число' : 'Текст'}{q.required ? ' · Обов\'язкове' : ''}
                      </Typography>
                      <Typography variant="body1">
                        Відповідь: {renderAnswerValue(answer)}
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          ) : (
            <Typography>Немає даних</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResponseDetail(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Surveys;


